import CancelIcon from '@mui/icons-material/Cancel';
import { useState, useEffect, useCallback, useMemo } from 'react';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { foodBusinessCategories, usStates, USStatesEnum, USStatesCounties, CUSTOM_BUSINESS_TYPE_VALUE, CUSTOM_BUSINESS_TYPE_LABEL, isListedFoodBusinessType } from '../../utils/enums';
import { Step, BusinessFormData, MyBusinessSchema } from '../../data/types';
import { mapBusinessToFormData } from '../../utils/businessProfile';



type AddBusinessProps = {
  showIntro: boolean;
  handleSubmitForm: (data: BusinessFormData) => void;
  handleClose: () => void;
  setShowIntro: React.Dispatch<React.SetStateAction<boolean>>;
  editingBusiness: MyBusinessSchema | null;
  embedded?: boolean;
};

const steps: Step[] = [
  { id: 1, name: 'Business Stage', isCompleted: false },
  { id: 2, name: 'Business Details', isCompleted: false },
  { id: 3, name: 'Preferences', isCompleted: false},
];

// Form Constants
const KITCHEN_TIMEFRAMES = [
  'ASAP',
  '1 - 2 weeks',
  '3 - 4 weeks',
  '2 - 5 months',
  'Beyond 6 months'
] as const;

const KITCHEN_BUDGETS = [
  '$0 - $200',
  '$201 - $500',
  '$501 - $1000',
  '$1001 - $5000',
  'Beyond $5000'
] as const;

const KITCHEN_FREQUENCIES = [
  'One-time',
  'Weekly',
  'Monthly',
  'Quarterly',
  'Annually'
] as const;

const KITCHEN_SIZES = [
  'Small (Less than 500 square feet)',
  'Medium (Greater than 500 and less than 1000 square feet)',
  'Large (greater than 1000 square feet)'
] as const;

const KITCHEN_EQUIPMENT = [
  'Convection Oven', 'Commercial Oven', 'Rack Oven', 'Deck Oven',
  'Range', 'Induction Cooktops', 'Deep Fryer', 'Air Fryer',
  'Food Mixer', 'Food Dehydrator', 'Commercial Grinder', 'Blenders',
  'Food Processors', 'Prep Tables', 'Ice Cream Maker', 'Coffee Roaster',
  'Flash Freezer', 'Griddle', 'Food Slicer',
  'Steam Kettle', 'Tilt Skillet', 'Vertical Cutter/Mix', 'Charbroiler',
  'Bottling Line', 'Blast Freezer', 'Canning line', 'Water Chiller',
  'Proofer', 'Vacuum Sealer', 'Dough Sheeter',
  'Packaging Heat Seal'
] as const;

const KITCHEN_FEATURES = [
  'Food Truck Friendly', 'Parking', 'Event Space', '24/7 Access',
  'Temperature Control', 'Teaching Kitchen', 'Demo Kitchen', 'Retail Outlet',
  'Vegan Friendly', 'Gluten-Free Facility', 'Allergen Free Facility', 'USDA Certified Room',
  'Cold Storage', 'Freezer Storage', 'Dry Storage', 'Incubation',
  'Warehouse Storage', 'Business Consulting', 'Co-Packing', 'Distribution Support'
] as const;

const CHEF_OPTIONS = [
  { value: '1', label: 'Only one at a time' },
  { value: '2-3', label: '2-3 chefs' },
  { value: '5-10', label: '5-10 chefs' },
  { value: '10+', label: '10+ chefs' }
] as const;

const PAYMENT_FREQUENCIES = [
  'After every booking',
  'At the end of each week',
  'Bi-Weekly',
  'Monthly',
  'Quarterly',
  'Other'
] as const;

const KITCHEN_TYPES = [
  'Restaurant',
  'Commercial Kitchen',
  'Venue Kitchen',
  'Ghost Kitchen',
  'Cloud Kitchen',
  'Other'
] as const;

const AddBusiness: React.FC<AddBusinessProps> = ({ showIntro, handleSubmitForm, handleClose, setShowIntro, editingBusiness, embedded = false }) => {
  
  const [currentStep, setCurrentStep] = useState<Step>(steps[0]);

  const getExistingBusinessData = useCallback((): BusinessFormData => {
    return mapBusinessToFormData(editingBusiness);
  }, [editingBusiness]);

  const [formData, setFormData] = useState<BusinessFormData>(getExistingBusinessData());
  const defaultBusinessTypes = useMemo(() => 
    formData.businessCategory !== -1 ? foodBusinessCategories[formData.businessCategory].types : [],
    [formData.businessCategory]
  );
  const [businessTypes, setBusinessTypes] = useState<string[]>(defaultBusinessTypes);
  const [isCustomBusinessType, setIsCustomBusinessType] = useState(false);

  useEffect(() => {
    setBusinessTypes(defaultBusinessTypes);
  }, [defaultBusinessTypes]);


  useEffect(() => {
    if (editingBusiness) {
      const mapped = getExistingBusinessData();
      setFormData(mapped);
      const types = mapped.businessCategory !== -1
        ? foodBusinessCategories[mapped.businessCategory].types
        : [];
      setIsCustomBusinessType(!isListedFoodBusinessType(mapped.businessType, types));
    } else {
      setIsCustomBusinessType(false);
    }
  }, [editingBusiness, getExistingBusinessData]);



  const proceedDisabled = (
    (currentStep.id === 1 && !formData.businessStage) || 
    (currentStep.id === 2 && (
      !formData.businessName || 
      formData.businessCategory === -1 || 
      !formData.businessType.trim() || 
      !formData.yearsOfOperation)
    ) || 
    (currentStep.id === 3 && (
      !formData.cookingState || 
      !formData.cookingCounty ||
      !formData.cookingCity ||
      !formData.cookingZipcode)
    )
  );

  const handleProceed = () => {
    if (showIntro){
      setShowIntro(false);
    } else if (currentStep.id < steps.length){
      if (!proceedDisabled){
        setCurrentStep(steps[currentStep.id]);
      }
    } else if (currentStep.id === steps.length){
      if (!proceedDisabled){
        // formData.businessCategory = foodBusinessCategories[formData.businessCategory as number].category;
        handleSubmitForm({ ...formData, businessType: formData.businessType.trim() });
      }
    }
  };

  const handleBack = () => {
    if (currentStep.id > 1){
      setCurrentStep((prevStep: Step) => steps[prevStep.id - 2]);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'businessCategory') {
      const categoryIndex = parseInt(value);
      if (categoryIndex >= 0 && categoryIndex < foodBusinessCategories.length) {
        const categoryObject = foodBusinessCategories[categoryIndex];
        setBusinessTypes(categoryObject.types);
        setIsCustomBusinessType(false);
        setFormData((prev: BusinessFormData) => ({
          ...prev,
          businessCategory: categoryIndex,
          businessType: categoryObject.types[0]
        }));
      }
    } else if (name === 'businessType') {
      if (value === CUSTOM_BUSINESS_TYPE_VALUE) {
        setIsCustomBusinessType(true);
        setFormData((prev: BusinessFormData) => ({ ...prev, businessType: '' }));
        return;
      }
      setIsCustomBusinessType(false);
      setFormData((prev: BusinessFormData) => ({ ...prev, businessType: value }));
    } else if (name === 'customBusinessType') {
      setFormData((prev: BusinessFormData) => ({ ...prev, businessType: value }));
    } else if ((name && value) || (['businessName', 'description', 'cookingCity', 'cookingZipcode'].includes(name) && value.trim() === '')) {
      setFormData((prev: BusinessFormData) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const renderStepsProgress = () => (
    <div className="flex items-center justify-between mb-8 overflow-x-auto">
      {steps.map((step) => (
        <div key={step.id} className="flex items-center">
          <div className={`flex items-center ${step.id === currentStep.id ? 'text-primaryDark-300' : 'text-gray-400'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${
              step.id === currentStep.id ? 'bg-primaryDark-300 text-white' : 'bg-gray-200'
            }`}>
              {step.id}
            </div>
            <span className="ml-2 text-sm whitespace-nowrap">{step.name}</span>
          </div>
          {step.id < steps.length && (
            <div className="mx-4 h-[1px] w-8 bg-gray-300" />
          )}
        </div>
      ))}
    </div>
  );

  const getBusinessDetailsProgress = () => {
    let completed = 0;
    const total = 5; // Required fields: name, category, type, years

    if (formData.businessName) completed++;
    if (formData.businessCategory !== -1) completed++;
    if (formData.businessType) completed++;
    if (formData.yearsOfOperation) completed++;
    if (formData.description) completed++;
    return { completed, total };
  };

  const renderBusinessStageForm = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-black">What stage is your food business?</h2>

      <div className="space-y-4">
        <label className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:border-gray-300">
          <input
            type="radio"
            name="businessStage"
            value="dream"
            checked={formData.businessStage === 'dream'}
            onChange={handleFormChange}
            className="mt-1 text-primaryDark-300 ring-primaryDark-300"
          />
          <div>
            <h3 className="font-medium text-black">Dream Stage</h3>
            <p className="text-gray-600">I'm thinking about starting a food business.</p>
          </div>
        </label>

        <label className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:border-gray-300">
          <input
            type="radio"
            name="businessStage"
            value="build"
            checked={formData.businessStage === 'build'}
            onChange={handleFormChange}
            className="mt-1 text-primaryDark-300 ring-primaryDark-300"
          />
          <div>
            <h3 className="font-medium text-black">Build Stage</h3>
            <p className="text-gray-600">I've started but haven't fully set up my food business.</p>
          </div>
        </label>

        <label className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:border-gray-300">
          <input
            type="radio"
            name="businessStage"
            value="scale"
            checked={formData.businessStage === 'scale'}
            onChange={handleFormChange}
            className="mt-1 text-primaryDark-300 ring-primaryDark-300"
          />
          <div>
            <h3 className="font-medium text-black">Scale Stage</h3>
            <p className="text-gray-600">I have a food business and want to grow it further.</p>
          </div>
        </label>
      </div>

      <div className="flex justify-end">
        <button 
          onClick={handleProceed}
          className="font-bold px-6 py-2 bg-primaryDark-200 border-[1px] hover:border-primaryDark-300 border-primaryDark-200  text-white rounded-md hover:bg-primaryDark-300 disabled:bg-primaryDark-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          disabled={proceedDisabled}
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderBusinessDetailsForm = () => {
    const progress = getBusinessDetailsProgress();
    
    return (
      <div className="space-y-6">
        <div>
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Business Details</h2>
            <span className="text-sm text-gray-600">{progress.completed}/{progress.total} Questions Completed</span>
          </div>
          <p className="text-sm text-gray-600 mt-1">Lets get to know more about your business</p>
          <p className="text-sm text-gray-600">Questions with * are required</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="businessName"
              value={formData.businessName}
              onChange={handleFormChange}
              placeholder="Enter your business name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business logo
            </label>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-[#EFF8FA] flex items-center justify-center border border-gray-200 shrink-0">
                {formData.logo ? (
                  <img src={formData.logo} alt="Business logo preview" className="w-full h-full object-cover bg-white" />
                ) : (
                  <span className="text-xs text-gray-400">Logo</span>
                )}
              </div>
              <label className="cursor-pointer inline-flex items-center px-3 py-2 rounded-md border border-gray-300 text-sm font-medium text-primaryDark-100 hover:bg-gray-50">
                Upload logo
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const previewUrl = URL.createObjectURL(file);
                    setFormData((prev: BusinessFormData) => ({
                      ...prev,
                      logo: previewUrl,
                      logoFile: file,
                    }));
                  }}
                />
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Category <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                name="businessCategory"
                value={formData.businessCategory}
                onChange={handleFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none"
              >
                <option value={-1}>
                  -- Select Business Category --
                </option>
                {foodBusinessCategories.map((category: { category: string }, index: number) => (
                  <option key={index} value={index}>
                    {category.category}
                  </option>
                ))}
              </select>
              {/* <KeyboardArrowDownIcon className="absolute right-2 top-2 text-gray-400" /> */}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type of Business <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                name="businessType"
                value={isCustomBusinessType ? CUSTOM_BUSINESS_TYPE_VALUE : formData.businessType}
                onChange={handleFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none"
                data-testid="business-type-select"
              >
                <option value="">
                  -- Select Business Type --
                </option>
                {businessTypes.map((type: string, index: number) => (
                  <option key={index} value={type}>
                    {type}
                  </option>
                ))}
                <option value={CUSTOM_BUSINESS_TYPE_VALUE}>{CUSTOM_BUSINESS_TYPE_LABEL}</option>
              </select>
              {/* <KeyboardArrowDownIcon className="absolute right-2 top-2 text-gray-400" /> */}
            </div>
            {isCustomBusinessType ? (
              <input
                type="text"
                name="customBusinessType"
                value={formData.businessType}
                onChange={handleFormChange}
                placeholder="Enter your business type"
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="custom-business-type-input"
              />
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Years of Operation <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                name="yearsOfOperation"
                value={formData.yearsOfOperation}
                onChange={handleFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none"
              >
                <option value="">
                  -- Select Years of Operation --
                </option>
                <option value="0-1">0-1 years</option>
                <option value="1-3">1-3 years</option>
                <option value="3-5">3-5 years</option>
                <option value="5+">5+ years</option>
              </select>
              {/* <KeyboardArrowDownIcon className="absolute right-2 top-2 text-gray-400" /> */}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleFormChange}
              placeholder="Give a brief description of your business"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button 
            onClick={handleBack}
            className="font-bold px-6 py-2 bg-white hover:bg-[#E9E9E9] mr-4 text-primaryDark-100 rounded-md transition-colors border-[1px] border-[#E9E9E9]"
          >
            Back
          </button>
          <button 
            onClick={handleProceed}
            className="font-bold px-6 py-2 bg-primaryDark-200 border-[1px] border-primaryDark-200 hover:border-primaryDark-300 text-white rounded-md hover:bg-primaryDark-300 disabled:bg-primaryDark-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            disabled={proceedDisabled}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  const renderPreferencesForm = () => (
    <div className="space-y-6 h-[calc(100vh-200px)] sm:h-[650px] flex flex-col">
      <div>
        <h2 className="text-2xl font-semibold text-black">Preferences</h2>
        <p className="text-gray-600 mt-2">Tell us about your needs to help us find the perfect match.</p>
      </div>

      <div className="flex-1 overflow-y-auto pr-4 space-y-6">
        {/* Cooking Location */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-900">
            Where would you be cooking? <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div className="relative">
              <select
                name="cookingState"
                value={formData.cookingState}
                onChange={handleFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryDark-300 focus:border-primaryDark-300 appearance-none bg-white"
              >
                <option value="">
                  -- Select a state --
                </option>
                {usStates.map((state: string) => (
                  <option key={state} value={USStatesEnum[state]?.abbreviation}>
                    {USStatesEnum[state]?.fullName}
                  </option>
                ))}
              </select>
              {/* <KeyboardArrowDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" /> */}
            </div>
            
            {formData.cookingState && USStatesCounties[formData.cookingState] && (
              <div className="relative">
                <select
                  name="cookingCounty"
                  value={formData.cookingCounty}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryDark-300 focus:border-primaryDark-300 appearance-none bg-white"
                >
                  <option value="">
                    -- Select {['DC'].includes(formData.cookingState) ? 'Ward' : 'County'} --
                  </option>
                  <option value="statewide">All</option>
                  {USStatesCounties[formData.cookingState].map((county: string, i: number) => (
                    <option key={i} value={county}>{county}</option>
                  ))}
                </select>
                {/* <KeyboardArrowDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" /> */}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <input
              name="cookingCity"
              value={formData.cookingCity || ''}
              onChange={handleFormChange}
              placeholder="City"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryDark-300 focus:border-primaryDark-300 bg-white"
            />
            <input
              name="cookingZipcode"
              value={formData.cookingZipcode || ''}
              onChange={handleFormChange}
              placeholder="ZIP code"
              inputMode="numeric"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryDark-300 focus:border-primaryDark-300 bg-white"
            />
          </div>
        </div>

        {/* Kitchen Timeframe */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-900">
            How soon do you need a kitchen?
          </label>
          <div className="relative">
            <select
              name="kitchenTimeframe"
              value={formData.kitchenTimeframe}
              onChange={handleFormChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryDark-300 focus:border-primaryDark-300 appearance-none bg-white"
            >
              <option value="">
                -- Select Timeframe --
              </option>
              {KITCHEN_TIMEFRAMES.map((timeframe) => (
                <option key={timeframe} value={timeframe}>{timeframe}</option>
              ))}
            </select>
            {/* <KeyboardArrowDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" /> */}
          </div>
        </div>

        {/* Kitchen Budget */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-900">
            How much would you like to spend on a kitchen (per week)?
          </label>
          <div className="relative">
            <select
              name="kitchenBudget"
              value={formData.kitchenBudget}
              onChange={handleFormChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryDark-300 focus:border-primaryDark-300 appearance-none bg-white"
            >
              <option value="">
                -- Select Budget Range --
              </option>
              {KITCHEN_BUDGETS.map((budget) => (
                <option key={budget} value={budget}>{budget}</option>
              ))}
            </select>
            {/* <KeyboardArrowDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" /> */}
          </div>
        </div>

        {/* Kitchen Frequency */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-900">
            How often would you need a kitchen?
          </label>
          <div className="relative">
            <select
              name="kitchenFrequency"
              value={formData.kitchenFrequency}
              onChange={handleFormChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryDark-300 focus:border-primaryDark-300 appearance-none bg-white"
            >
              <option value="">
                -- Select Frequency --
              </option>
              {KITCHEN_FREQUENCIES.map((frequency) => (
                <option key={frequency} value={frequency}>{frequency}</option>
              ))}
            </select>
            {/* <KeyboardArrowDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" /> */}
          </div>
        </div>

        {/* Kitchen Size */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-900">
            What size of kitchen would you need?
          </label>
          <div className="relative">
            <select
              name="kitchenSize"
              value={formData.kitchenSize}
              onChange={handleFormChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryDark-300 focus:border-primaryDark-300 appearance-none bg-white"
            >
              <option value="">
                -- Select Kitchen Size --
              </option>
              {KITCHEN_SIZES.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            {/* <KeyboardArrowDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" /> */}
          </div>
        </div>

        {/* Kitchen Equipment */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-900">
            What kitchen equipment would you need?
          </label>
          <div className="grid grid-cols-3 gap-3 h-auto p-1">
            {KITCHEN_EQUIPMENT.map((equipment) => {
              const selected = (formData.kitchenEquipment || []).includes(equipment);
              return (
                <button
                  type="button"
                  key={equipment}
                  role="checkbox"
                  aria-checked={selected}
                  onClick={() => {
                    setFormData((prev: BusinessFormData) => {
                      const current = prev.kitchenEquipment || [];
                      return {
                        ...prev,
                        kitchenEquipment: selected
                          ? current.filter((item: string) => item !== equipment)
                          : [...current, equipment],
                      };
                    });
                  }}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all text-left ${
                    selected
                      ? 'border-primaryDark-300 bg-primaryDark-50 ring-1 ring-primaryDark-300'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-sm font-medium">{equipment}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Kitchen Features */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-900">
            What features do you need?
          </label>
          <div className="grid grid-cols-3 gap-3 p-1">
            {KITCHEN_FEATURES.map((feature) => {
              const selected = (formData.kitchenFeatures || []).includes(feature);
              return (
                <button
                  type="button"
                  key={feature}
                  role="checkbox"
                  aria-checked={selected}
                  onClick={() => {
                    setFormData((prev: BusinessFormData) => {
                      const current = prev.kitchenFeatures || [];
                      return {
                        ...prev,
                        kitchenFeatures: selected
                          ? current.filter((item: string) => item !== feature)
                          : [...current, feature],
                      };
                    });
                  }}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all text-left ${
                    selected
                      ? 'border-primaryDark-300 bg-primaryDark-50 ring-1 ring-primaryDark-300'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-sm font-medium">{feature}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Number of Chefs */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-900">
            How many chefs can you have cooking at once in your kitchen?
          </label>
          <div className="relative">
            <select
              name="maxChefs"
              value={formData.maxChefs}
              onChange={handleFormChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryDark-300 focus:border-primaryDark-300 appearance-none bg-white"
            >
              <option value="">
                -- Select Number of Chefs --
              </option>
              {CHEF_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {/* <KeyboardArrowDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" /> */}
          </div>
        </div>

        {/* Payment Frequency */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-900">
            How frequently would you like to receive payments?
          </label>
          <div className="relative">
            <select
              name="paymentFrequency"
              value={formData.paymentFrequency}
              onChange={handleFormChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryDark-300 focus:border-primaryDark-300 appearance-none bg-white"
            >
              <option value="">
                -- Select Payment Frequency --
              </option>
              {PAYMENT_FREQUENCIES.map((frequency) => (
                <option key={frequency} value={frequency}>{frequency}</option>
              ))}
            </select>
            {/* <KeyboardArrowDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" /> */}
          </div>
        </div>

        {/* Kitchen Type */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-900">
            What type of kitchen do you have?
          </label>
          <div className="relative">
            <select
              name="kitchenType"
              value={formData.kitchenType}
              onChange={handleFormChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryDark-300 focus:border-primaryDark-300 appearance-none bg-white"
            >
              <option value="">
                -- Select Kitchen Type --
              </option>
              {KITCHEN_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            {/* <KeyboardArrowDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" /> */}
          </div>
        </div>
      </div>


      <div className="flex justify-end pt-6 border-t mt-6">
        <button 
            onClick={handleBack}
            className="font-bold px-6 py-2 bg-white hover:bg-[#E9E9E9] mr-4 text-primaryDark-100 rounded-md transition-colors border-[1px] border-[#E9E9E9]"
           
        >
          Back
        </button>
        <button 
          onClick={handleProceed}
          className="font-bold px-6 py-2 bg-primaryDark-200 border-[1px] border-primaryDark-200 hover:border-primaryDark-300 text-white rounded-md hover:bg-primaryDark-300 transition-colors disabled:bg-primaryDark-200 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={proceedDisabled}
        >
          Submit
        </button>
      </div>
    </div>
  );

  return (
    <div className={embedded ? 'w-full bg-transparent' : 'p-6 max-w-3xl mx-auto bg-white rounded-lg'}>
      {!embedded ? (
        <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer mb-4">
          <CancelIcon className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
        </span>
      ) : null}

      {showIntro && !embedded ? (
        <div className="space-y-6">
          <div>
            <h2 className="text-[24px] font-semibold text-gray-900 font-raleway">To Add your Food Business</h2>
            <p className="text-[16px] text-[#4D4D4D] font-open-sans mt-1">These are the steps required to add your business</p>
          </div>

          <div className="space-y-6">
            {/* Step 1 */}
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 rounded-full bg-primaryDark-300 flex items-center justify-center text-white text-sm">
                  1
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-[18px] font-medium text-black font-raleway">Fill out a form</h3>
                <p className="text-[16px] text-gray-600 font-open-sans">You would fill out a form</p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 rounded-full bg-primaryDark-300 flex items-center justify-center text-white text-sm">
                  2
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-[18px] font-medium text-black font-raleway">Get grouped</h3>
                <p className="text-[16px] text-gray-600 font-open-sans">Choose the stage that best reflects where your business is today; your readiness profile will use that selection.</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 rounded-full bg-primaryDark-300 flex items-center justify-center text-white text-sm">
                  3
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-[18px] font-medium text-black font-raleway">Manage your business</h3>
                <p className="text-[16px] text-gray-600 font-open-sans">You get access to your personalized business dashboard</p>
              </div>
            </div>
          </div>

          <button 
            onClick={handleProceed}
            className="w-full py-3 px-4 font-bold text-[16px] font-raleway bg-primaryDark-200 text-white rounded-md hover:bg-primaryDark-300 border-0 transition-colors"
            disabled={proceedDisabled}
          >
            Proceed to Add my Business
          </button>
        </div>
      ) : (
        <>
          {
            renderStepsProgress()
          }
          {
            (currentStep.id === 1) ? renderBusinessStageForm() : (
              (currentStep.id === 2) ? renderBusinessDetailsForm() : (
                (currentStep.id === 3) ? renderPreferencesForm() : <></>
              )
            )
          }
        </>
      )}
    </div>
  );
};

export default AddBusiness;